import { useEffect } from 'react'
import { HeartIcon, StarIcon, CheckCircleIcon } from 'lucide-react'
import { TestimonialsColumn } from '@components/ui/testimonials-columns-1'
import { supabase } from '@lib/supabase'
import { motion } from 'motion/react'
import { useState } from 'react'

const galleryImages = [
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/cfb6bab8-0fda-49cc-7e8e-c18159b01b00/public',
    alt: 'Wedding photography'
  },
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/071f78e7-c9e5-4c45-27df-76c8e99c8900/public',
    alt: 'Wedding couple portrait'
  },
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/f9596bb2-b075-4cef-4891-f7a87685f200/public',
    alt: 'Portrait session'
  },
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/c7ed3be9-7f76-4dee-19e0-d0dc95463f00/public',
    alt: 'Family photography'
  },
  {
    src: '/training/focus/Screenshot 2025-11-29 at 17.47.06.png',
    alt: 'Event photography'
  },
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/065eb3c0-5554-4377-c14b-d336b4a49900/public',
    alt: 'Newborn photography'
  },
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/e5ca4928-a9c7-46ee-d49d-90896eae7700/public',
    alt: 'Professional photography'
  },
  {
    src: 'https://imagedelivery.net/Gf6TASBWPw1R5GcTIFvyjg/cec0e770-9046-4fe6-2d16-420c7baba200/public',
    alt: 'Celebration photography'
  }
]

const fallbackLocations = [
  'Los Angeles, CA',
  'New York, NY',
  'Chicago, IL',
  'Austin, TX',
  'Seattle, WA',
  'Miami, FL',
  'Denver, CO',
  'Portland, OR'
]

function RedditLeadForm() {
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]')
    if (existingScript) return

    const script = document.createElement('script')
    script.src = 'https://link.msgsndr.com/js/form_embed.js'
    script.async = true
    document.body.appendChild(script)
  }, [])

  return (
    <iframe
      src="https://api.leadconnectorhq.com/widget/form/vkWWt6MGQRTJFQfawACc"
      style={{ width: '100%', height: '100%', border: 'none', borderRadius: '3px' }}
      id="inline-vkWWt6MGQRTJFQfawACc"
      data-layout="{'id':'INLINE'}"
      data-trigger-type="alwaysShow"
      data-trigger-value=""
      data-activation-type="alwaysActivated"
      data-activation-value=""
      data-deactivation-type="neverDeactivate"
      data-deactivation-value=""
      data-form-name="Reddit - Raffle Free Engagement Session Form"
      data-height="748"
      data-layout-iframe-id="inline-vkWWt6MGQRTJFQfawACc"
      data-form-id="vkWWt6MGQRTJFQfawACc"
      title="Reddit - Raffle Free Engagement Session Form"
      className="block min-h-[748px] w-full"
    />
  )
}

function SocialProofPhotos() {
  return (
    <section className="bg-background py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Real moments, beautifully captured
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            A quick look at the kind of sessions couples and families book with Love & Photos.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {galleryImages.map((image, index) => (
            <div key={image.src} className={index % 2 === 1 ? 'pt-8' : ''}>
              <img
                src={image.src}
                alt={image.alt}
                className="h-48 w-full rounded-2xl object-cover shadow-sm transition-transform duration-300 hover:scale-[1.02] sm:h-56"
              />
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <span>2,500+ Photographers</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            <span>4.9 Rating</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <HeartIcon className="h-4 w-4 fill-primary text-primary" />
            <span>10,000+ Couples</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function Testimonials() {
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data: reviews, error } = await supabase
          .from('reviews')
          .select(`
            id,
            comment,
            rating,
            created_at,
            users:reviewer_id (
              full_name,
              avatar_url
            )
          `)
          .eq('is_verified', true)
          .gte('rating', 4)
          .order('created_at', { ascending: false })
          .limit(20)

        if (error) {
          console.error('Supabase error:', error)
          setLoading(false)
          return
        }

        const transformedReviews = (reviews || [])
          .filter(review => review.comment && review.comment.trim() !== '')
          .map((review, index) => {
            const metadata = review.users?.metadata || {}
            const location = metadata.city && metadata.state
              ? `${metadata.city}, ${metadata.state}`
              : metadata.city || metadata.state || fallbackLocations[index % fallbackLocations.length]

            return {
              text: review.comment,
              image: review.users?.avatar_url || '',
              name: review.users?.full_name || 'Verified Customer',
              location
            }
          })
          .slice(0, 12)

        setTestimonials(transformedReviews)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching reviews:', error)
        setLoading(false)
      }
    }

    fetchReviews()
  }, [])

  const columnsNeeded = 3
  const testimonialsPerColumn = testimonials.length > 0
    ? Math.ceil(testimonials.length / columnsNeeded)
    : 0

  const firstColumn = testimonials.slice(0, testimonialsPerColumn)
  const secondColumn = testimonials.slice(testimonialsPerColumn, testimonialsPerColumn * 2)
  const thirdColumn = testimonials.slice(testimonialsPerColumn * 2)

  return (
    <section className="relative bg-background pb-20">
      <div className="container z-10 mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="mx-auto flex max-w-[540px] flex-col items-center justify-center"
        >
          <div className="flex justify-center">
            <div className="rounded-lg border px-4 py-1">Testimonials</div>
          </div>

          <h2 className="lp-h2 mt-5">What our customers say</h2>
          <p className="mt-5 text-center opacity-75">
            {loading
              ? 'Loading testimonials...'
              : testimonials.length === 0
                ? 'Be the first to share your experience!'
                : 'See what our customers have to say about Love & Photos.'}
          </p>
        </motion.div>

        <div className="mt-10 flex max-h-[740px] justify-center gap-6 overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)]">
          {loading ? (
            <>
              {[0, 1, 2].map(column => (
                <div key={column} className={`${column > 0 ? 'hidden md:flex' : 'flex'} ${column > 1 ? 'lg:flex' : ''} flex-col gap-6`}>
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-full max-w-xs animate-pulse rounded-3xl border p-10 shadow-lg shadow-primary/10">
                      <div className="mb-2 h-4 w-full rounded bg-muted" />
                      <div className="mb-2 h-4 w-3/4 rounded bg-muted" />
                      <div className="h-4 w-5/6 rounded bg-muted" />
                      <div className="mt-5 flex items-center gap-2">
                        <div className="h-10 w-10 rounded-full bg-muted" />
                        <div>
                          <div className="mb-1 h-4 w-24 rounded bg-muted" />
                          <div className="h-3 w-20 rounded bg-muted" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : testimonials.length === 0 ? (
            <div className="py-20 text-center">
              <div className="mx-auto max-w-md">
                <h3 className="mb-2 text-lg font-semibold">No testimonials yet</h3>
                <p className="text-muted-foreground">
                  Check back soon to see what our customers are saying about Love & Photos!
                </p>
              </div>
            </div>
          ) : (
            <>
              <TestimonialsColumn testimonials={firstColumn} duration={15} />
              {secondColumn.length > 0 && (
                <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={19} />
              )}
              {thirdColumn.length > 0 && (
                <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={17} />
              )}
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default function RedditLanding() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-rose-50/70 via-background to-background px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 text-center">
            <div className="inline-flex rounded-full border border-primary/20 bg-white px-4 py-1 text-sm font-medium text-primary shadow-sm">
              Reddit raffle
            </div>
            <h1 className="mt-5 text-3xl font-bold tracking-tight text-foreground sm:text-5xl">
              Enter to win a free engagement session
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
              We are giving one couple a complimentary engagement session with Love & Photos.
            </p>
          </div>

          <div className="rounded-lg border bg-white p-2 shadow-xl">
            <RedditLeadForm />
          </div>
        </div>
      </section>

      <SocialProofPhotos />
      <Testimonials />
    </main>
  )
}
